/**
 * SendSurveyModal — Manager+ surface for broadcasting a Pulse
 * survey to employees.
 *
 * Two modes:
 *   1. "Everyone" — sends an in-app notification to every active
 *      employee in the org.
 *   2. "Specific people" — multi-select picker drawn from the
 *      directory; only the chosen employees get notified.
 *
 * Permission: anything that gets here was already gated by
 * ``canSend = role !== "EMPLOYEE"`` on the parent. The backend
 * additionally enforces SEND_PULSE so a hand-crafted request is
 * rejected with a 403.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Users, UserPlus, Send } from "lucide-react";

import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";

import { sendSurvey, type PulseSurvey } from "@/api/pulse";
import { listEmployees } from "@/api/employees";
import type { EmployeeListItem } from "@/types/employee";

interface Props {
  survey: PulseSurvey;
  onClose: () => void;
  onSent?: (count: number) => void;
}

type Target = "all" | "employees";

export function SendSurveyModal({ survey, onClose, onSent }: Props) {
  const { addToast } = useToast();
  const [target, setTarget] = useState<Target>("all");
  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Lazy-load the directory only when the user picks "Specific" so
  // the "Everyone" path stays a single click.
  useEffect(() => {
    if (target !== "employees" || employees.length > 0) return;
    let cancelled = false;
    setLoadingEmps(true);
    // Backend caps per_page at 100; for sending purposes that's
    // plenty for a manager nudging their team. If you need >100,
    // type into the search box (server-side filter).
    listEmployees(1, 100, { status: "ACTIVE" })
      .then((res) => {
        if (cancelled) return;
        setEmployees(res.items);
      })
      .catch(() => {
        if (cancelled) return;
        addToast("error", "Couldn't load the directory. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoadingEmps(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target, employees.length, addToast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => {
      const full = `${e.first_name} ${e.last_name}`.toLowerCase();
      return (
        full.includes(q) ||
        (e.work_email ?? "").toLowerCase().includes(q) ||
        (e.title ?? "").toLowerCase().includes(q)
      );
    });
  }, [employees, search]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSend = async () => {
    if (target === "employees" && selected.size === 0) {
      addToast(
        "error",
        "Pick at least one person, or switch to 'Everyone'.",
      );
      return;
    }
    setSending(true);
    try {
      const result = await sendSurvey(survey.id, {
        target,
        employee_ids:
          target === "employees" ? Array.from(selected) : undefined,
        message: message.trim() || undefined,
      });
      const skippedNote =
        result.skipped_count > 0
          ? ` (${result.skipped_count} skipped — no login on file)`
          : "";
      addToast(
        "success",
        `Sent to ${result.sent_count} ${result.sent_count === 1 ? "person" : "people"}${skippedNote}.`,
      );
      onSent?.(result.sent_count);
      onClose();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast(
        "error",
        ax.response?.data?.detail || "Couldn't send the survey.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Send "${survey.title}"`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Target picker */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setTarget("all")}
            className={`rounded-bentoSm border p-4 text-left transition-colors ${
              target === "all"
                ? "border-accent bg-accent/5"
                : "border-border hover:bg-surface-muted"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Users
                className={`h-4 w-4 ${
                  target === "all" ? "text-accent" : "text-text-muted"
                }`}
              />
              <span className="text-sm font-semibold text-text-primary">
                Everyone
              </span>
            </div>
            <p className="text-xs text-text-muted">
              Notify every active employee in the org.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setTarget("employees")}
            className={`rounded-bentoSm border p-4 text-left transition-colors ${
              target === "employees"
                ? "border-accent bg-accent/5"
                : "border-border hover:bg-surface-muted"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <UserPlus
                className={`h-4 w-4 ${
                  target === "employees" ? "text-accent" : "text-text-muted"
                }`}
              />
              <span className="text-sm font-semibold text-text-primary">
                Specific people
              </span>
            </div>
            <p className="text-xs text-text-muted">
              Pick one or more individuals — useful for targeted check-ins.
            </p>
          </button>
        </div>

        {/* Employee picker — only when target=employees */}
        {target === "employees" && (
          <div className="rounded-bentoSm border border-border p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-text-muted" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or title…"
                className="flex-1"
              />
              {selected.size > 0 && (
                <Badge variant="info">
                  {selected.size} selected
                </Badge>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-border/50">
              {loadingEmps ? (
                <div className="flex items-center justify-center py-6 text-sm text-text-muted">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading directory…
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-text-muted text-center py-6">
                  {search
                    ? "No matches."
                    : "No active employees in the directory."}
                </p>
              ) : (
                filtered.map((emp) => {
                  const checked = selected.has(emp.id);
                  return (
                    <label
                      key={emp.id}
                      className="flex items-center gap-3 py-2 px-1 cursor-pointer hover:bg-surface-muted rounded"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(emp.id)}
                        className="h-4 w-4 rounded border-border accent-accent"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">
                          {emp.first_name} {emp.last_name}
                        </div>
                        <div className="text-xs text-text-muted truncate">
                          {emp.title || "—"}
                          {emp.work_email && ` · ${emp.work_email}`}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Optional custom message */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Message (optional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            maxLength={500}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
            placeholder={`Quick note — defaults to: "New pulse survey: ${survey.title}. Takes a minute."`}
          />
          <p className="text-xs text-text-muted mt-1">
            {message.length}/500
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            isLoading={sending}
            disabled={target === "employees" && selected.size === 0}
          >
            <Send className="h-4 w-4" />
            Send
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default SendSurveyModal;
